import React, { useEffect, useState, useCallback, useMemo } from "react";
import styles from "./BottomFindBar.module.css";
import { findApproxMatches } from "../wasmFunctions";
import FindSequenceForm from "./FindSequenceForm.js";
import FindResultsNav from "./FindResultsNav.js";

/**
 * Sliding "Find sequence" panel.
 * - Searches `haystack` for `querySequence` allowing up to `maxAllowedMismatches`.
 * - Displays navigation across matches; updates selection via `onSelectRange`.
 *
 * External API unchanged.
 */
export default function BottomFindBar({
  isOpen,
  onClose,
  haystack = "",
  onSelectRange,
}) {
  //#region Local state
  const [querySequence, setQuerySequence] = useState("");
  const [maxAllowedMismatches, setMaxAllowedMismatches] = useState(0);
  const [matchStartIndices, setMatchStartIndices] = useState([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Remember the last successfully "executed" search inputs
  const [lastQuery, setLastQuery] = useState("");
  const [lastMismatches, setLastMismatches] = useState(0);

  const hasResults = matchStartIndices.length > 0;

  // If user edits query or mismatch count, show "Find" again (no need to press Done)
  const hasPendingSearch = useMemo(
    () =>
      querySequence !== lastQuery || maxAllowedMismatches !== lastMismatches,
    [querySequence, maxAllowedMismatches, lastQuery, lastMismatches]
  );
  //#endregion

  //#region Lifecycle: close on Escape when open
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);
  //#endregion

  //#region Selection helpers
  const selectMatchByIndex = useCallback(
    (idx, { scroll = true } = {}) => {
      if (!hasResults) return;
      let i = idx;
      if (i < 0) i = matchStartIndices.length - 1;
      if (i >= matchStartIndices.length) i = 0;
      setActiveMatchIndex(i);
      const start = matchStartIndices[i];
      const end = start + querySequence.length - 1;
      onSelectRange && onSelectRange({ start, end });
      if (scroll) {
        // DisplayDualStrand handles scrolling when selection changes.
      }
    },
    [hasResults, matchStartIndices, querySequence.length, onSelectRange]
  );
  //#endregion

  //#region Form: submit search
  const handleFindSubmit = async (e) => {
    e.preventDefault();
    if (!querySequence) return;

    setIsSearching(true);
    try {
      const matches = await findApproxMatches(
        haystack,
        querySequence.toUpperCase(),
        +maxAllowedMismatches
      );
      setMatchStartIndices(matches);

      // Mark these inputs as the "executed" search so UI shows Done only if nothing changed
      setLastQuery(querySequence);
      setLastMismatches(maxAllowedMismatches);

      if (matches.length > 0) {
        setActiveMatchIndex(0);
        const start = matches[0];
        const end = start + querySequence.length - 1;
        onSelectRange && onSelectRange({ start, end }); // triggers scrolling in viewer
      } else {
        setActiveMatchIndex(null);
        onSelectRange && onSelectRange(null);
      }
    } catch (err) {
      console.error(err);
      setMatchStartIndices([]);
      setActiveMatchIndex(null);
      onSelectRange && onSelectRange(null);
      alert("Error running search: " + err);
    } finally {
      setIsSearching(false);
    }
  };
  //#endregion

  //#region Reset
  const handleDone = () => {
    // Clear results
    setMatchStartIndices([]);
    setActiveMatchIndex(null);
    onSelectRange && onSelectRange(null);

    // Clear the query text itself (as requested)
    setQuerySequence("");
    setLastQuery("");

    // Optionally keep maxAllowedMismatches as-is for convenience.
    // Also blur any focused input so the panel goes "inactive".
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
  };
  //#endregion

  //#region Navigation handlers
  const handlePrev = () =>
    selectMatchByIndex(
      (activeMatchIndex - 1 + matchStartIndices.length) %
        matchStartIndices.length
    );
  const handleNext = () =>
    selectMatchByIndex((activeMatchIndex + 1) % matchStartIndices.length);
  //#endregion

  //#region Render
  return (
    <div
      className={styles.root}
      data-open={isOpen ? "true" : "false"}
      aria-hidden={!isOpen}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="false"
        aria-label="Find sequence"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.grabber} />
          <div className={styles.titleArea}>
            <span className={styles.title}>Find sequence</span>
            <span className={styles.subtitle}>
              Use ← / → to jump between matches
            </span>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            type="button"
            aria-label="Close find"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Search form (split) */}
        <FindSequenceForm
          querySequence={querySequence}
          setQuerySequence={setQuerySequence}
          maxAllowedMismatches={maxAllowedMismatches}
          setMaxAllowedMismatches={setMaxAllowedMismatches}
          onSubmit={handleFindSubmit}
          isSearching={isSearching}
          hasResults={hasResults}
          hasPendingSearch={hasPendingSearch}
          onDone={handleDone}
        />

        {/* Results / navigation (split) */}
        <FindResultsNav
          hasResults={hasResults}
          isSearching={isSearching}
          querySequence={querySequence}
          activeMatchIndex={activeMatchIndex}
          matchStartIndices={matchStartIndices}
          onPrev={handlePrev}
          onNext={handleNext}
          hasPendingSearch={hasPendingSearch}
        />
      </div>
    </div>
  );
  //#endregion
}
